/**
 * Cliente HTTP minimalista para el backend AG Stratos.
 * - Inyecta automáticamente el Bearer token de localStorage.
 * - Lanza ApiError con status + body parseado para que el caller decida.
 * - Usa el proxy de Vite (/api → http://localhost:3000) en dev y la env
 *   VITE_API_BASE_URL en producción.
 * - Day 6 Block C: auto-refresh transparente al 401.
 */
export const API_BASE = (
  import.meta.env.VITE_API_BASE_URL ?? '/api/v1'
).replace(/\/$/, '');

const TOKEN_KEY = 'ag_stratos.token';
const REFRESH_KEY = 'ag_stratos.refreshToken';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t: string | null) => {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else   localStorage.removeItem(TOKEN_KEY);
};

export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);
export const setRefreshToken = (t: string | null) => {
  if (t) localStorage.setItem(REFRESH_KEY, t);
  else   localStorage.removeItem(REFRESH_KEY);
};

// ---------------------------------------------------------------------------
// Carril K — Sesión / idle (fix del logout a ~15 min)
// ---------------------------------------------------------------------------
// El access token es CORTO (15m) por seguridad y se renueva EN SILENCIO antes de
// expirar (scheduleSilentRefresh) → el borde de 15m es invisible durante el uso
// activo. La vida de la SESIÓN la gobierna la INACTIVIDAD, no el TTL del access:
//   base 1h · "recordar sesión" 12h (opt-in) · cap duro = refresh token 30d.
import {
  resolveIdleWindowMs, isExpiredByInactivity, nextRefreshDelayMs, clampTenantIdleMinutes,
  BASE_IDLE_MINUTES, MIN_REFRESH_DELAY_MS,
} from './session';

const LAST_ACTIVITY_KEY = 'ag_stratos.lastActivity';
const REMEMBER_KEY = 'ag_stratos.remember';        // '1' si "recordar sesión" (login opt-in)
const TENANT_BASE_KEY = 'ag_stratos.tenantIdleMin'; // ventana BASE del tenant (de /me)
const PREFILL_KEY = 'ag_stratos.prefill';          // {tenantSlug, email} (NO password)

export const getLastActivity = (): number => {
  const v = localStorage.getItem(LAST_ACTIVITY_KEY);
  return v ? Number(v) || 0 : 0;
};
/** Marca "ahora" como última actividad del usuario. */
export const touchActivity = () => {
  localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
};
export const clearActivity = () => localStorage.removeItem(LAST_ACTIVITY_KEY);

/** LOGIN — persiste el opt-in "recordar sesión" (default off). */
export function setSessionPolicy(remember: boolean): void {
  localStorage.setItem(REMEMBER_KEY, remember ? '1' : '0');
}
export const isRememberSession = (): boolean => localStorage.getItem(REMEMBER_KEY) === '1';

/** /me — persiste la ventana BASE del tenant (config del admin). Valida lista blanca. */
export function setTenantIdleBase(minutes: number | null | undefined): void {
  localStorage.setItem(TENANT_BASE_KEY, String(clampTenantIdleMinutes(minutes)));
}
export function getTenantIdleBaseMinutes(): number {
  const v = Number(localStorage.getItem(TENANT_BASE_KEY));
  return clampTenantIdleMinutes(v) === v ? v : BASE_IDLE_MINUTES;
}

/** Ventana de inactividad efectiva (ms): base del tenant + "recordar" (≥12h). */
export function getIdleWindowMs(): number {
  return resolveIdleWindowMs(isRememberSession(), getTenantIdleBaseMinutes());
}

/**
 * True si la sesión superó su ventana de inactividad. Sin marca previa (0) =
 * false (sesión nueva o anterior a esta feature → no cortar en seco).
 */
export const isSessionExpiredByIdle = (): boolean =>
  isExpiredByInactivity(getLastActivity(), getIdleWindowMs(), Date.now());

// ---- Pre-fill: recordar tenant + correo (NO la contraseña) -----------------
export function getPrefill(): { tenantSlug: string; email: string } | null {
  try {
    const raw = localStorage.getItem(PREFILL_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return typeof p?.tenantSlug === 'string' && typeof p?.email === 'string' ? p : null;
  } catch { return null; }
}
export function setPrefill(tenantSlug: string, email: string): void {
  localStorage.setItem(PREFILL_KEY, JSON.stringify({ tenantSlug, email }));
}
/** "Usar otra cuenta": limpia el pre-fill. */
export function clearPrefill(): void {
  localStorage.removeItem(PREFILL_KEY);
}

// ---------------------------------------------------------------------------
// TREN 2 (11/09) — acting de piso en TODOS los requests (costura única).
// El FloorLockProvider lo setea al desbloquear y lo limpia al bloquear; api()
// manda el header X-Floor-Acting y el backend resuelve autoría UNA vez por
// request (interceptor). Los caminos por-endpoint (body/query) quedaron muertos.
// ---------------------------------------------------------------------------
let floorActingToken: string | null = null;
export function setFloorActingToken(t: string | null): void { floorActingToken = t; }

export class ApiError extends Error {
  constructor(public status: number, public body: any, message: string) {
    super(message);
    this.name = 'ApiError';
  }
  /**
   * V11 / Day 4 — código de error del backend cuando el trial expiró.
   * El backend lo manda como `body.code === 'TRIAL_EXPIRED'` en un 403.
   */
  get isTrialExpired(): boolean {
    return this.status === 403 && this.body?.code === 'TRIAL_EXPIRED';
  }
}

/** Suscriptores que escuchan eventos de trial expirado (mostrar /trial-expired). */
type TrialExpiredListener = (info: { expiredAt: string | null; message: string }) => void;
const trialExpiredListeners = new Set<TrialExpiredListener>();
export function onTrialExpired(fn: TrialExpiredListener): () => void {
  trialExpiredListeners.add(fn);
  return () => trialExpiredListeners.delete(fn);
}

/** Day 6 Block C — listener para sesión expirada (refresh falló). */
type AuthLostListener = () => void;
const authLostListeners = new Set<AuthLostListener>();
export function onAuthLost(fn: AuthLostListener): () => void {
  authLostListeners.add(fn);
  return () => authLostListeners.delete(fn);
}

// ---------------------------------------------------------------------------
// 🔴 "NADA FALLA EN SILENCIO" (regla 2026-09-13) — bus global de errores de
// MUTACIÓN. Origen: el 500 de /fire no se veía en pantalla; Arturo creía que la
// comanda se había enviado. Toda mutación (POST/PUT/PATCH/DELETE) que responda
// 4xx/5xx publica aquí un mensaje claro y accionable; `ToastHost` lo muestra
// (toast con createPortal, JAMÁS window.alert). Es la RED: dispara aunque el
// caller no tenga catch, así que ningún error de escritura queda mudo.
// Excluidos (tienen su propio flujo): 401 (onAuthLost) y 403 TRIAL_EXPIRED
// (onTrialExpired). Un caller que YA muestra el error inline puede pasar
// `{ silentError: true }` para no duplicar el toast.
// ---------------------------------------------------------------------------
export interface ApiMutationError { status: number; message: string; method: string; path: string; }
type ApiErrorListener = (e: ApiMutationError) => void;
const apiErrorListeners = new Set<ApiErrorListener>();
export function onApiMutationError(fn: ApiErrorListener): () => void {
  apiErrorListeners.add(fn);
  return () => apiErrorListeners.delete(fn);
}
const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
function friendlyByStatus(status: number): string {
  if (status >= 500) return 'El servidor falló al procesar la acción. Reintenta en un momento.';
  if (status === 409) return 'Conflicto: el dato cambió o ya existe. Recarga y vuelve a intentar.';
  if (status === 404) return 'No se encontró el recurso (pudo eliminarse). Recarga la vista.';
  if (status === 403) return 'No tienes permiso para esta acción.';
  if (status === 400 || status === 422) return 'No se pudo completar: revisa los datos e intenta de nuevo.';
  return `No se pudo completar la acción (${status}).`;
}
function fireApiMutationError(path: string, method: string, err: ApiError): void {
  if (!MUTATION_METHODS.has(method)) return;   // solo mutaciones (los reads suelen tener fallback)
  if (err.status === 401) return;              // sesión → onAuthLost
  if (err.isTrialExpired) return;              // → onTrialExpired
  const raw = err.body?.message;
  const backend = typeof raw === 'string' ? raw : Array.isArray(raw) ? raw.join(' · ') : null;
  const message = backend && backend.length <= 200 ? backend : friendlyByStatus(err.status);
  apiErrorListeners.forEach((fn) => { try { fn({ status: err.status, message, method, path }); } catch { /* swallow */ } });
}

export interface ApiOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** El caller ya muestra el error inline → no dupliques el toast global. */
  silentError?: boolean;
}

/**
 * Hace el fetch + parse + ApiError throw. Compartido entre `api` y `api`
 * post-refresh para evitar duplicar el manejo de errores.
 */
async function doFetch<T>(path: string, opts: ApiOpts, token: string | null): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(floorActingToken ? { 'X-Floor-Acting': floorActingToken } : {}),
      ...(opts.headers ?? {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = new ApiError(res.status, data, data?.message ?? res.statusText);
    if (err.isTrialExpired) {
      const info = {
        expiredAt: (data?.expiredAt as string) ?? null,
        message:   (data?.message as string) ?? 'Trial expirado',
      };
      trialExpiredListeners.forEach((fn) => { try { fn(info); } catch { /* swallow */ } });
    }
    throw err;
  }
  return data as T;
}

/**
 * Day 6 Block C / fix(session-persistence) — refresh token robusto.
 *
 * Distinguimos el motivo del fallo para NO tumbar la sesión por un blip:
 *  - 'renewed'    → access nuevo (y refresh rotado) guardados.
 *  - 'invalid'    → el refresh token fue RECHAZADO (401/403): expiró/lo invalidaron
 *                   → logout real.
 *  - 'transient'  → red caída / 5xx / respuesta ilegible: NO desloguear, mantener la
 *                   sesión y dejar que el caller falle suave (el próximo request reintenta).
 *  - 'no-refresh' → no hay refresh token guardado → no hay forma de renovar → logout.
 *
 * Concurrency: un único refresh en vuelo (refreshInFlight) — N requests 401
 * simultáneos esperan al MISMO promise (evita la tormenta de refresh/logout). El
 * backend ROTA el refresh en cada llamada, así que disparar varios en paralelo
 * invalidaría tokens entre sí; el single-flight lo previene.
 */
type RefreshOutcome =
  | { status: 'renewed'; token: string }
  | { status: 'invalid'; code?: string }
  | { status: 'transient' }
  | { status: 'no-refresh' };

/**
 * V219 — por qué se cerró la sesión, para que el login lo diga en lugar de un error
 * genérico: el backend responde 401 con code ACCESS_CHANGED (rol, reset, transferencia) o
 * ACCOUNT_PROTECTED (soporte congeló la cuenta). Se guarda en sessionStorage antes del
 * redirect y el login lo lee una vez.
 */
export type AuthNotice = 'access_changed' | 'protected';
export const AUTH_NOTICE_KEY = 'ag_stratos.authNotice';
export function authNoticeFromCode(code: unknown): AuthNotice | null {
  if (code === 'ACCESS_CHANGED') return 'access_changed';
  if (code === 'ACCOUNT_PROTECTED') return 'protected';
  return null;
}
export function takeAuthNotice(): AuthNotice | null {
  try {
    const v = sessionStorage.getItem(AUTH_NOTICE_KEY);
    sessionStorage.removeItem(AUTH_NOTICE_KEY);
    return v === 'access_changed' || v === 'protected' ? v : null;
  } catch {
    return null;
  }
}
function rememberAuthNotice(n: AuthNotice | null): void {
  if (!n) return;
  try { sessionStorage.setItem(AUTH_NOTICE_KEY, n); } catch { /* sin storage */ }
}

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

async function refreshOnce(refresh: string): Promise<RefreshOutcome> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ refreshToken: refresh }),
    });
  } catch {
    return { status: 'transient' }; // error de red → transitorio
  }
  if (res.ok) {
    let data: any = null;
    try { data = await res.json(); } catch { return { status: 'transient' }; }
    if (!data?.accessToken) return { status: 'transient' };
    setToken(data.accessToken);
    if (data.refreshToken) setRefreshToken(data.refreshToken);
    return { status: 'renewed', token: data.accessToken };
  }
  // SOLO un 401/403 del propio /auth/refresh significa "refresh inválido/expirado".
  if (res.status === 401 || res.status === 403) {
    let code: string | undefined;
    try { code = (await res.json())?.code; } catch { /* cuerpo vacío */ }
    return { status: 'invalid', code };
  }
  // 5xx / 408 / 429 / cold-start, etc. → transitorio: no cerramos sesión.
  return { status: 'transient' };
}

let refreshInFlight: Promise<RefreshOutcome> | null = null;
async function doRefresh(): Promise<RefreshOutcome> {
  if (refreshInFlight) return refreshInFlight;
  const refresh = getRefreshToken();
  if (!refresh) return { status: 'no-refresh' };
  refreshInFlight = (async () => {
    try {
      let r = await refreshOnce(refresh);
      // Reintento ÚNICO solo ante fallo transitorio (nunca ante 'invalid').
      if (r.status === 'transient') {
        await delay(500);
        const latest = getRefreshToken(); // otra pestaña pudo rotarlo entre tanto
        if (latest) r = await refreshOnce(latest);
      }
      return r;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * Logout "suave" one-shot: si varios requests 401 caen a la vez con el refresh ya
 * inválido, limpiamos tokens y notificamos UNA sola vez (no N redirects).
 */
let authLostFired = false;
function fireAuthLost(): void {
  if (authLostFired) return;
  authLostFired = true;
  stopSilentRefresh();
  setToken(null);
  setRefreshToken(null);
  authLostListeners.forEach((fn) => { try { fn(); } catch { /* swallow */ } });
}

// ---------------------------------------------------------------------------
// Carril K — Refresh PROACTIVO silencioso (el fix del corte a ~15 min)
// ---------------------------------------------------------------------------
// Renueva el access ANTES de expirar (nextRefreshDelayMs), así el uso activo
// nunca choca contra el borde de 15m. Cross-tab-safe y NO-destructivo:
//  - single-flight (doRefresh) + re-lectura del refresh token de localStorage;
//  - un fallo del refresh PROACTIVO nunca desloguea (solo el 401 reactivo lo hace);
//  - se re-agenda al rotar el token en OTRA pestaña (evento 'storage') y al volver
//    la pestaña a foco (los timers vienen throttled en background).
let silentTimer: ReturnType<typeof setTimeout> | null = null;

export function stopSilentRefresh(): void {
  if (silentTimer != null) { clearTimeout(silentTimer); silentTimer = null; }
}

export function scheduleSilentRefresh(): void {
  stopSilentRefresh();
  const delay = nextRefreshDelayMs(getToken(), Date.now());
  if (delay == null) return; // token opaco/ausente → el 401 reactivo es la red de seguridad
  silentTimer = setTimeout(async () => {
    silentTimer = null;
    if (!getToken()) return;               // deslogueado entre tanto
    if (isSessionExpiredByIdle()) return;  // idle → AuthProvider corta; no renovar
    // ¿otra pestaña ya lo rotó? si el token sigue fresco (delay > piso), re-agendar.
    const d2 = nextRefreshDelayMs(getToken(), Date.now());
    if (d2 != null && d2 > MIN_REFRESH_DELAY_MS) { scheduleSilentRefresh(); return; }
    const r = await doRefresh();
    // Proactivo = NO desloguea. renewed/transient → re-agenda; invalid/no-refresh →
    // parar (el próximo request real cae en el 401 reactivo y hace el logout limpio).
    if (r.status === 'renewed' || r.status === 'transient') scheduleSilentRefresh();
  }, delay);
}

if (typeof window !== 'undefined') {
  // Otra pestaña rotó el access (localStorage compartido) → re-agendar con el nuevo exp.
  window.addEventListener('storage', (e) => {
    if (e.key === TOKEN_KEY && getToken()) scheduleSilentRefresh();
  });
  // Al volver la pestaña a foco, re-evaluar (los timers se throttlean en background).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && getToken()) scheduleSilentRefresh();
  });
}

/**
 * Refresh tolerante COMPARTIDO por todos los requests (api / apiUpload / apiDownload).
 * Ejecuta `run(token)`; si lanza un 401, refresca el access (single-flight) y reintenta
 * UNA vez con el token nuevo. Mismo comportamiento que tenía `api` inline:
 *  - anti-carrera multi-pestaña (si otra pestaña ya rotó el access, se reusa sin refrescar);
 *  - 'transient' (red/5xx/cold-start del refresh) → NO desloguea, re-lanza el 401 original;
 *  - 'invalid' | 'no-refresh' → logout real (fireAuthLost).
 * `run` se re-invoca por intento, así que cada reintento arma su propio fetch (incl.
 * FormData fresco en subidas) y la descarga solo dispara su <a download> en el intento OK.
 * `path` solo sirve para NO intentar refrescar sobre los propios `/auth/*` (evita loop).
 */
async function withAuthRetry<T>(path: string, run: (token: string | null) => Promise<T>): Promise<T> {
  const token = getToken();
  try {
    return await run(token);
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err;
    if (path.startsWith('/auth/')) throw err;

    // Anti-carrera: otra pestaña (o un refresh en vuelo) ya renovó el access → reusa.
    const current = getToken();
    if (current && current !== token) {
      return await run(current);
    }

    // Refresh transparente y TOLERANTE a fallos transitorios.
    const r = await doRefresh();
    if (r.status === 'renewed') {
      return await run(r.token);
    }
    if (r.status === 'transient') {
      // Blip del refresh: NO cerramos sesión — tokens intactos, la acción falla suave.
      throw err;
    }
    // 'invalid' | 'no-refresh' → logout real, con último chequeo anti-carrera por rotación.
    const afterToken = getToken();
    if (afterToken && afterToken !== token) {
      return await run(afterToken);
    }
    // V219: el motivo viene del 401 original o del refresh rechazado.
    rememberAuthNotice(authNoticeFromCode(err.body?.code) ?? authNoticeFromCode(r.status === 'invalid' ? r.code : null));
    fireAuthLost();
    throw err;
  }
}

/**
 * §22 — subida multipart (adjuntos). FormData NO lleva Content-Type manual (el
 * navegador pone el boundary). Reusa el Bearer token; lanza ApiError igual que `api`.
 */
export async function apiUpload<T = unknown>(
  path: string,
  file: File,
  field = 'file',
): Promise<T> {
  try {
    return await withAuthRetry(path, async (token) => {
      const form = new FormData();
      form.append(field, file);
      const res = await fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: form,
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) throw new ApiError(res.status, data, data?.message ?? res.statusText);
      return data as T;
    });
  } catch (err) {
    if (err instanceof ApiError) fireApiMutationError(path, 'POST', err); // subida = mutación
    throw err;
  }
}

/**
 * §22 — descarga autenticada (adjuntos). El binario NO se sirve por ruta estática
 * pública (aislamiento cross-tenant), así que hay que mandar el Bearer y disparar
 * la descarga vía blob + <a download>.
 */
export async function apiDownload(path: string, fileName: string): Promise<void> {
  return withAuthRetry(path, async (token) => {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const body = text ? (() => { try { return JSON.parse(text); } catch { return null; } })() : null;
      throw new ApiError(res.status, body, body?.message ?? res.statusText);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  });
}

export async function api<T = unknown>(path: string, opts: ApiOpts = {}): Promise<T> {
  try {
    return await withAuthRetry(path, (token) => doFetch<T>(path, opts, token));
  } catch (err) {
    // 🔴 Nada falla en silencio: toda mutación 4xx/5xx surfacea (aunque el caller
    // no tenga catch). El caller puede silenciar el toast con { silentError: true }.
    if (err instanceof ApiError && !opts.silentError) {
      fireApiMutationError(path, opts.method ?? 'GET', err);
    }
    throw err;
  }
}
